import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { JOB_PORTAL_KEYS, NS } from '../../../../../i18n/constants/keys';
import { calculateDaysLeftCount } from '../../functions';
import { getContentLangName } from '../functions';

interface IJobPostHeaderProps {
  jobTitle?: string;
  companyLogo?: string | null;
  endDate?: number | string;
  contentLanguages: string[];
  contentLang: string;
  onContentLangChange: (lang: string) => void;
}

export const JobPostHeader = ({
  jobTitle,
  companyLogo,
  endDate,
  contentLanguages,
  contentLang,
  onContentLangChange,
}: IJobPostHeaderProps) => {
  const { t } = useTranslation(NS.jobPortal);
  const [isLogoBroken, setIsLogoBroken] = React.useState<boolean>(false);
  const daysLeft = calculateDaysLeftCount(endDate);

  return (
    <Paper component="header" elevation={0} sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ mx: 'auto', maxWidth: '75rem', px: { xs: 2, sm: 3, lg: 4 }, py: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'flex-start' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 48, sm: 'auto' },
                minWidth: 48,
                maxWidth: { xs: 80, sm: 160 },
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                px: 1.25,
                py: 0.5,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {companyLogo && !isLogoBroken ? (
                <Box
                  component="img"
                  src={companyLogo}
                  alt="Company Logo"
                  onError={() => setIsLogoBroken(true)}
                  sx={{
                    maxHeight: 34,
                    maxWidth: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : (
                <BusinessCenterIcon fontSize="small" color="action" />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Chip
                icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', ml: 1 }} />}
                label={t(JOB_PORTAL_KEYS.details.hiring)}
                size="small"
                color="success"
                sx={{ mb: 0.5, fontWeight: 700 }}
              />
              <Typography variant="h2" noWrap>
                {jobTitle}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            {contentLanguages.length >= 1 && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                  View in:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                      value={contentLang}
                      onChange={(e) => onContentLangChange(e.target.value)}
                      inputProps={{ 'aria-label': 'Content language' }}
                      sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    {contentLanguages.map((lang) => (
                      <MenuItem key={lang} value={lang} sx={{ fontSize: '0.75rem' }}>
                        {getContentLangName(lang)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}

            <Chip
              icon={<AccessTimeIcon fontSize="small" />}
              label={`${daysLeft} days left to apply`}
              color="success"
              variant="filled"
              sx={{ fontWeight: 700 }}
            />
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};
